import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import FeatureGates from "@atlassian/feature-gate-js-client";

import {
    OrangeCard,
    PurpleCard,
    GreenCard,
    BlueCard,
    BlackCard
} from "./components/assets";
import {
    getHeroCardDesignImage,
    getHeroCardItImage,
    getHeroCardMarketingImage,
    getHeroCardOperationsImage,
    getHeroCardSoftwareDevelopmentImage
} from "./components/assets/localized-images";
import { HeroCard } from "./components/hero-card";
import { HeroContextProvider } from "./util/hero-context";

import { JswProductModalCarousel } from ".";

const heroCardsStyling = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    width: "100%",
    maxWidth: "fit-content",
    gap: "16px",
    overflowX: "auto",
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    padding: "64px 10px 10px 10px",
    scrollbarColor: "#AEAEAE rgba(0,0,0,0)",
    [media.below.lg]: {
        paddingBlockStart: "54px",
        justifyContent: "flex-start",
        paddingBlockEnd: "10px",
        marginBlockStart: "3px",
        overflowX: "scroll",
        scrollPaddingTop: "3px"
    }
});

type HeroCardText = {
    eyebrow: string;
    title: string;
    image: string;
    label: string;
    BackgroundImage: () => JSX.Element;
};

export const HeroCards = () => {
    const intl = useIntl();

    const showUpdatedHeroExperiment: boolean =
        FeatureGates.getExperimentValue<boolean>(
            "organic_jira_product_tour_overview_hero_v2",
            "showExperiment",
            false
        );

    const softwareCard = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-cards-software-development-card.eyebrow-text",
            defaultMessage: "product & issue tracking"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-cards-software-development-card.title-text",
            defaultMessage: "Software Development"
        }),
        image: getHeroCardSoftwareDevelopmentImage(intl.locale),
        BackgroundImage: OrangeCard,
        label: "software-development"
    };

    const marketingCard = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-cards-marketing-card.eyebrow-text",
            defaultMessage: "plan & launch campaigns"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-cards-marketing-card.title-text",
            defaultMessage: "Marketing"
        }),
        image: getHeroCardMarketingImage(intl.locale),
        BackgroundImage: PurpleCard,
        label: "marketing"
    };

    const itCard = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-cards-it-card.eyebrow-text",
            defaultMessage: "manage & track requests"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-cards-it-card.title-text",
            defaultMessage: "IT"
        }),
        image: getHeroCardItImage(intl.locale),
        BackgroundImage: GreenCard,
        label: "it"
    };

    const designCard = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-cards-design-card.eyebrow-text",
            defaultMessage: "build creative workflows"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-cards-design-card.title-text",
            defaultMessage: "Design"
        }),
        image: getHeroCardDesignImage(intl.locale),
        BackgroundImage: BlueCard,
        label: "design"
    };

    const operationsCard = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-cards-operations-card.eyebrow-text",
            defaultMessage: "create custom processes"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-cards-operations-card.title-text",
            defaultMessage: "Operations"
        }),
        image: getHeroCardOperationsImage(intl.locale),
        BackgroundImage: BlackCard,
        label: "operations"
    };

    const heroCardText: HeroCardText[] = [
        softwareCard,
        marketingCard,
        itCard,
        designCard,
        operationsCard
    ];

    const experimentHeroCardText = [
        {
            ...softwareCard,
            eyebrow: "software development",
            title: "Project & issue tracking"
        },
        {
            ...marketingCard,
            eyebrow: "marketing",
            title: "Plan & launch campaigns"
        },
        {
            ...itCard,
            eyebrow: "it services",
            title: "Manage & track requests"
        },
        {
            ...designCard,
            eyebrow: "design",
            title: "Build creative workshops"
        },
        {
            ...operationsCard,
            eyebrow: "operations",
            title: "Create custom processes"
        }
    ];

    const updatedCopyArray = showUpdatedHeroExperiment
        ? experimentHeroCardText
        : heroCardText;

    return (
        <HeroContextProvider>
            <div css={heroCardsStyling}>
                {updatedCopyArray.map((card, index) => (
                    <HeroCard
                        key={index}
                        eyebrow={card.eyebrow}
                        title={card.title}
                        image={card.image}
                        BackgroundImage={
                            card.BackgroundImage as () => JSX.Element
                        }
                        idx={index}
                        label={card.label}
                        showUpdatedHeroExperiment={showUpdatedHeroExperiment}
                    />
                ))}
                <JswProductModalCarousel />
            </div>
        </HeroContextProvider>
    );
};
